/* eslint-disable react/no-danger */
/* eslint-disable no-nested-ternary, no-param-reassign */
import { Alert, Search, Skeleton, Tile } from '@pmi/dsm-react';
import jwtDecode from 'jwt-decode';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import { useDispatch, useSelector } from 'react-redux';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { includes, sanitize } from '../../shared/helpers';
import useDebounce from '../../shared/hooks/useDebounce';
import { useGetProductsQuery } from './apiSlice';
import EnrollmentCard from './EnrollmentCard';
import ProgressCard from './ProgressCard';
import Welcome from './Welcome';
import styles from '../../shared/dsm.module.scss';
import productCardStyles from './productcard.module.scss';
import {
  reportError,
  reportSearch,
  signalRToastWithAnalytics,
} from '../../shared/analytics';
import { Product } from './models';
import {
  adminInfoSelector,
  setAdminDetails,
} from '../../shared/store/shared/adminInfoSlice';
import { userSelector } from '../../shared/store/shared/userSlice';
import { JwtDecodedUser } from '../../shared/models/UserModels';
import useSignalREventConnector from '../../shared/hooks/useSignalRConnector';
import {
  MembershipSku01,
  MembershipSku02,
  PAY_BY_USER_STATUS_MAP,
  ProductTypes,
} from '../../shared/constants';
import CongratsModal from './EnrollmentCard/ProductModal/EnhancedEnrollmentModal/CongratsModal';
import { isMarExperience } from './EnrollmentCard/ProductModal/EnhancedEnrollmentModal/helpers';

interface OrdersFullfilledResponse {
  Sku: string;
  ParentSku: string;
}

export default function TrainingHub() {
  const { value: user } = useSelector(userSelector);
  const accessToken = user ? user.access_token : '';
  const authUser: JwtDecodedUser = accessToken ? jwtDecode(accessToken) : null;
  const myProductsRef = useRef<HTMLDivElement>(undefined);
  const dispatch = useDispatch();
  const { accountId } = useParams<{ accountId: string }>();
  const [userProductSilentRefreshState, setUserProductSilentRefreshState] =
    useState(false);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const { data, isLoading, isError, isSuccess, refetch, isFetching } =
    useGetProductsQuery(
      { accountId },
      {
        skip:
          !accountId ||
          !authUser?.b2b_aid ||
          !authUser?.b2b_auid ||
          authUser.b2b_aid.toLocaleUpperCase() !==
            accountId.toLocaleUpperCase(),
      },
    );

  const [searchInput, setSearchInput] = useState('');

  const [products, setProducts] = useState({
    my: [],
    company: [],
    filtered: [],
  });
  const [headerTitles, setHeaderTitles] = useState({
    my: 'My Redemptions',
    products: 'Available to Redeem',
  });
  const debouncedSearch = useDebounce(searchInput, 300);
  const reportingSearch = useDebounce(searchInput, 1500);

  const pMIMembershipProduct = useMemo(() => {
    const userProducts = data?.userProducts ?? [];
    const memb01 = userProducts.find(
      (p) => p.type === ProductTypes.membership && p.sku === MembershipSku01,
    );
    const memb02 = userProducts.find(
      (p) => p.type === ProductTypes.membership && p.sku === MembershipSku02,
    );
    return memb01 ?? memb02 ?? null;
  }, [data]);

  const getProductsInfo = (productData: Product[]) => {
    const my = productData.filter((prod) => prod.addToMyProducts);
    const company = productData.filter((prod) => !prod.addToMyProducts);
    const sanitizedSearch = sanitize(debouncedSearch);
    const filtered = debouncedSearch
      ? company.filter(
          (prod) =>
            includes(sanitize(prod.name), sanitizedSearch) ||
            includes(sanitize(prod.shortDescription), sanitizedSearch) ||
            includes(sanitize(prod.fullDescription), sanitizedSearch),
        )
      : company;
    return { my, company, filtered };
  };
  const { adminDetails } = useSelector(adminInfoSelector);
  const contactInfoMessage = `Please contact your administrator ${adminDetails} for further assistance.`;
  const allUserDealsDisabledMessage = `There are no products available because your organization’s agreement with PMI is disabled. ${contactInfoMessage}`;
  const allDealsBeyondActivationWindowMessage = `There are no products available because your organization’s agreement with PMI has expired. ${contactInfoMessage}`;
  const allDealsAheadOfActivationWindowMessage = `There are no products available because your organization’s agreement with PMI has not begun. ${contactInfoMessage}`;
  const otherMessage = `Your Platform link is not currently available. ${contactInfoMessage}`;

  useEffect(() => {
    // check if adminDetails state is empty and if it has product query has data
    if (!adminDetails && data && data?.adminInfo) {
      dispatch(setAdminDetails(data.adminInfo));
    }
  }, [data]);

  const errorMessage = 'Error getting products';
  useEffect(() => {
    if (isError) reportError(errorMessage);
  }, [isError]);

  useEffect(() => {
    if (data) {
      setProducts(getProductsInfo(data?.userProducts ?? []));
      if (data?.userProducts) {
        const payByUserOnly = data.userProducts.filter(
          (p) => p.isPayByUserProduct && p.type !== ProductTypes.membership,
        );
        const orgMembershipOnly = data.userProducts.filter(
          (p) => !p.isPayByUserProduct && p.type !== ProductTypes.membership,
        );
        if (
          payByUserOnly.length ===
          data.userProducts.filter((p) => p.type !== ProductTypes.membership)
            .length
        ) {
          setHeaderTitles({
            my: 'My Purchases',
            products: 'Available to Purchase',
          });
        }
        if (payByUserOnly.length > 0 && orgMembershipOnly.length > 0) {
          setHeaderTitles({
            my: 'My Redemptions/Purchases',
            products: 'Available to Redeem/Purchase',
          });
        }
      }
    }
  }, [isSuccess, isLoading, isFetching]);

  useEffect(() => {
    // do not show a loading indicator when its refetch and userProductsSilentRefetch = true for just one time
    // i.e perform a silent UI rerender
    // once refetch is completed revert the flag back to false
    // so that it can continue to show loading indicator on refetch
    // this flag is currently set to true by orderscompleted signalr update, for one time silent refetch without UI rerender
    if (userProductSilentRefreshState && !isFetching) {
      setUserProductSilentRefreshState(false);
    }
  }, [isFetching]);

  useEffect(() => {
    const prod = getProductsInfo(data?.userProducts ?? []);
    setProducts(prod);
  }, [debouncedSearch]);

  useEffect(() => {
    if (reportingSearch) {
      const success = products.filtered.length > 0;
      reportSearch({
        action: success ? 'search-success' : 'search-no-results',
        searchLoad: {
          searchTerm: reportingSearch,
          searchPagination: success ? 1 : 0,
          searchResults: products.filtered.length,
        },
      });
    }
  }, [reportingSearch]);

  const handleOrderFulfilled = (response: OrdersFullfilledResponse) => {
    setTimeout(() => {
      setUserProductSilentRefreshState(true);
      refetch()
        .then((res) => {
          const product = res?.data?.userProducts?.find(
            (e) => e.sku === response.Sku || e.sku === response.ParentSku,
          );
          signalRToastWithAnalytics(
            <>
              <span dangerouslySetInnerHTML={{ __html: product?.name }} /> is
              now available! The product is accessible below or on myPMI.
            </>,
            'success',
            `${product?.name} is now available!. The product is accessible below or on myPMI.`,
          );
          if (
            product &&
            isMarExperience(product.experienceType) &&
            res?.data?.userProducts?.filter(
              (p) =>
                p.type !== 'Membership' &&
                p.callToAction &&
                !p.redeemed &&
                !p.callToAction.text
                  ?.toLowerCase()
                  .trim()
                  .includes(PAY_BY_USER_STATUS_MAP.inCart),
            ).length > 0
          ) {
            setShowCongratsModal(true);
          }
        })
        .catch(() => {
          window.location.reload(); // reload the page if refetch fails
        });
    }, 5000);
  };

  useSignalREventConnector('orderfulfilled', handleOrderFulfilled);

  if (isLoading || (isFetching && !userProductSilentRefreshState)) {
    return (
      <div data-testid="loading-indicator">
        <div className="d-flex flex-wrap align-items-stretch">
          {[0, 1].map((i) => (
            <div
              className="d-grid col-md-6 col-12 pe-0 pe-md-4 pt-4 pt-md-0"
              key={i}
            >
              <Tile>
                <div className="row mb-3">
                  <div className="ps-3" style={{ width: 85 }}>
                    <Skeleton
                      animation="wave"
                      variant="circle"
                      width={50}
                      height={50}
                    />
                  </div>
                  <div className="col p-0">
                    <Skeleton animation="wave" height={20} width="50%" />
                    <Skeleton animation="wave" height={20} width="40%" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Skeleton animation="wave" height={20} />
                    <Skeleton animation="wave" height={20} />
                    <Skeleton animation="wave" height={20} width="80%" />
                  </div>
                </div>
              </Tile>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="loading-error">
        <Alert variant="danger" alertText={errorMessage} icon={IconDanger} />
      </div>
    );
  }

  return (
    <div>
      <Welcome accountId={accountId} adminInfo={data.adminInfo} />
      {products.my.length > 0 && (
        <div
          className="mb-3"
          data-adoberegion="b2bhub_myproducts"
          id="b2bhub_myproducts"
          ref={myProductsRef}
        >
          <div className="mb-3">
            <b>{headerTitles.my}</b>
          </div>
          <div className="d-flex flex-wrap align-items-stretch">
            {products.my
              .filter((x) => x.sku.includes('MEMB-B-'))
              .sort((x) => (x.sku === 'MEMB-B-01' ? -1 : 1))
              .map((product) => (
                <ProgressCard
                  product={product}
                  key={product.sku}
                  onSuccess={refetch}
                  accountId={accountId}
                />
              ))}
            {products.my
              .filter(
                (x) =>
                  x.type === ProductTypes.membership &&
                  !x.sku.includes('MEMB-B-'),
              )
              .sort((x) => (x.sku === '101010' ? -1 : 1))
              .map((product) => (
                <ProgressCard
                  product={product}
                  key={product.sku}
                  onSuccess={refetch}
                  accountId={accountId}
                />
              ))}
            {products.my
              .filter((x) => x.type !== ProductTypes.membership)
              .map((product) => (
                <ProgressCard
                  product={product}
                  key={product.sku}
                  onSuccess={refetch}
                  accountId={accountId}
                />
              ))}
          </div>
        </div>
      )}
      {products.company.length > 0 && (
        <div data-adoberegion="b2bhub_allproducts">
          <div className="d-flex-inline d-sm-flex flex-row justify-content-between mb-3 align-items-center">
            <b>{headerTitles.products}</b>
            <Search
              data-testid="products-search"
              id="products-search"
              className={`${styles.secondarySearch}`}
              placeholder="Search Products"
              value={searchInput}
              onClear={() => setSearchInput('')}
              onChange={(e: FormEvent<HTMLInputElement>) =>
                setSearchInput(e.currentTarget.value)
              }
            />
          </div>
          {/*
            We need this hidden element to ensure that
            addToCartMemberShipProduct works even if the product
            isn’t shown in the search results
            */}
          {pMIMembershipProduct !== null ? (
            <div
              style={{ display: 'none' }}
              data-component-id={`hidden-${pMIMembershipProduct.sku}`}
            >
              <EnrollmentCard
                product={pMIMembershipProduct}
                key={`hidden-${pMIMembershipProduct.sku}`}
                onSuccess={refetch}
                accountId={accountId}
                isFiltered={!!debouncedSearch}
              />
            </div>
          ) : null}
          <div
            className={`d-flex flex-wrap align-items-stretch ${productCardStyles.wrapper}`}
          >
            {products.filtered.length ? (
              <>
                {products.filtered
                  .filter((x) => x.sku.includes('MEMB-B-'))
                  .sort((x) => (x.sku === 'MEMB-B-01' ? -1 : 1))
                  .map((product) => (
                    <EnrollmentCard
                      product={product}
                      key={product.sku}
                      onSuccess={refetch}
                      accountId={accountId}
                      isFiltered={!!debouncedSearch}
                    />
                  ))}
                {products.filtered
                  .filter(
                    (x) =>
                      x.type === ProductTypes.membership &&
                      !x.sku.includes('MEMB-B-'),
                  )
                  .sort((x) => (x.sku === '101010' ? -1 : 1))
                  .map((product) => (
                    <EnrollmentCard
                      product={product}
                      key={product.sku}
                      onSuccess={refetch}
                      accountId={accountId}
                      isFiltered={!!debouncedSearch}
                    />
                  ))}
                {products.filtered
                  .filter((x) => x.type !== ProductTypes.membership)
                  .map((product) => (
                    <EnrollmentCard
                      product={product}
                      key={product.sku}
                      onSuccess={refetch}
                      accountId={accountId}
                      isFiltered={!!debouncedSearch}
                    />
                  ))}
              </>
            ) : (
              <div className="text-center h4 w-100 my-4">
                There are no search results matching “<b>{debouncedSearch}</b>”.
                <br />
                Please check your spelling or try another search.
              </div>
            )}
          </div>
        </div>
      )}
      {!products.my.length && !products.company.length && (
        <div className="text-center h4 w-100 my-4">
          {data.allUserDealsDisabled
            ? allUserDealsDisabledMessage
            : data.allDealsAheadOfActivationWindow
            ? allDealsAheadOfActivationWindowMessage
            : data.allDealsBeyondActivationWindow
            ? allDealsBeyondActivationWindowMessage
            : otherMessage}
        </div>
      )}
      {showCongratsModal && (
        <CongratsModal
          availableProducts={data?.userProducts ?? []}
          onCancel={() => setShowCongratsModal(false)}
        />
      )}
    </div>
  );
}
